import { RefObject, useCallback, useMemo, useRef, useState } from 'react'
import { FadeInBox } from '@components/Transitions'
import useResizeObserver from '@react-hook/resize-observer'
import { MotionStack, Stack } from '@ui'
import { AnimatePresence, Reorder } from 'framer-motion'
import { useDevice } from 'hooks/useDevice'
import { UploadStatus } from 'routes/EditSpaceMetadata/hooks/useUploadSpaceMetadataMedia'
import { MediaFormValue } from 'routes/EditSpaceMetadata/schema/spaceMetadata.schema'
import { atoms } from 'ui/styles/atoms.css'
import { vars } from 'ui/styles/vars.css'

import { MediaThumb, ProgressThumb, UploadThumb } from './MediaThumb'

type UploadingStatus = Extract<UploadStatus, { status: 'uploading' }>

export const MediaThumbs = (props: {
    onFileDialog: () => void
    onReorder: (newOrderIds: string[]) => void
    onRemoveItem: (fieldId: string) => void
    onSelectItem: (fieldId: string) => void
    fields: Array<{ id: string } & MediaFormValue>
    canAddMoreMedia: boolean
    pendingUploads: UploadingStatus[]
    selectedId: string | undefined
}) => {
    const {
        onFileDialog,
        onReorder,
        onRemoveItem,
        onSelectItem,
        fields,
        canAddMoreMedia,
        pendingUploads,
        selectedId,
    } = props

    const fieldIds = useMemo(() => fields.map((field) => field.id), [fields])

    const numThumbs = fields.length + pendingUploads.length + (fields.length > 0 ? 1 : 0)
    const numPlaceholders = Math.max(3 - numThumbs, 0)

    return (
        <MediaThumbContainer>
            <Reorder.Group
                as="div"
                axis="y"
                values={fieldIds}
                className={atoms({
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'sm',
                    width: '100%',
                })}
                onReorder={onReorder}
            >
                {fields.map((field, index) => {
                    const media = fields[index]

                    return (
                        <Reorder.Item
                            key={field.key}
                            value={field.id}
                            drag="y"
                            whileDrag={{ scale: 1.1 }}
                            className={atoms({
                                cursor: 'grab',
                            })}
                        >
                            <MediaThumb
                                key={field.key}
                                index={index}
                                fieldId={field.id}
                                media={media}
                                isSelected={selectedId === field.id}
                                onRemoveItem={onRemoveItem}
                                onSelectItem={onSelectItem}
                            />
                        </Reorder.Item>
                    )
                })}
            </Reorder.Group>

            <MotionStack gap="sm" layout="position">
                {pendingUploads.map((upload) => (
                    <ProgressThumb key={upload.id} fileName={upload.fileName} />
                ))}

                {Array.from({ length: numPlaceholders })
                    .map((_, index) => `${index}`)
                    .map((key) => (
                        <UploadThumb
                            key={key}
                            displayIcon={false}
                            onFileDialog={canAddMoreMedia ? onFileDialog : undefined}
                        />
                    ))}

                {canAddMoreMedia && fields.length > 0 && (
                    <UploadThumb displayIcon onFileDialog={onFileDialog} />
                )}
            </MotionStack>
        </MediaThumbContainer>
    )
}

export const MediaThumbContainer = (props: { children: React.ReactNode }) => {
    const { isTouch } = useDevice()
    return isTouch ? (
        <HorizontalMediaThumbContainer {...props} />
    ) : (
        <VerticalMediaThumbContainer {...props} />
    )
}
const VerticalMediaThumbContainer = (props: { children: React.ReactNode }) => {
    const [scrollState, setScrollState] = useState(() => ({ top: false, bottom: false }))

    const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
        const top = scrollTop > 0
        const bottom = Math.round(scrollTop) < Math.round(scrollHeight - clientHeight)
        setScrollState((s) => {
            if (s.top === top && s.bottom === bottom) {
                return s
            }
            return {
                top,
                bottom,
            }
        })
    }, [])

    // update gradients on resize
    const scrollRef = useRef<HTMLDivElement | null>(null)
    const ref = useRef<HTMLDivElement | null>(null)
    useResizeObserver(ref as RefObject<HTMLElement>, (entry) => {
        scrollRef.current?.dispatchEvent(new Event('scroll'))
    })

    return (
        <Stack
            minWidth={{ default: 'x10', touch: 'x7' }}
            position="relative"
            overflow="hidden"
            ref={ref}
        >
            <Stack
                absoluteFill
                ref={scrollRef}
                minWidth={{ default: 'x10', touch: 'x6' }}
                position="relative"
                overflow="scroll"
                gap={props.children ? 'md' : 'none'}
                onScroll={onScroll}
            >
                {props.children}
            </Stack>
            <AnimatePresence>
                {scrollState.top && (
                    <FadeInBox
                        pointerEvents="none"
                        position="topLeft"
                        width={{ default: 'x10', touch: 'x6' }}
                        height={{ default: 'x10', touch: 'x6' }}
                        style={{
                            backgroundImage: `linear-gradient(to top, ${vars.color.background.level1Opacity0}, var(--background))`,
                        }}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {scrollState.bottom && (
                    <FadeInBox
                        fast
                        pointerEvents="none"
                        position="bottomLeft"
                        width={{ default: 'x10', touch: 'x6' }}
                        height={{ default: 'x10', touch: 'x6' }}
                        style={{
                            backgroundImage: `linear-gradient(to bottom, ${vars.color.background.level1Opacity0}, var(--background))`,
                        }}
                    />
                )}
            </AnimatePresence>
        </Stack>
    )
}

const HorizontalMediaThumbContainer = (props: {
    children: React.ReactNode
    horizontal?: boolean
}) => {
    const [scrollState, setScrollState] = useState(() => ({ left: false, right: false }))

    const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const { scrollLeft: x, scrollWidth, clientWidth } = event.currentTarget
        const left = x > 0
        const right = Math.round(x) < Math.round(scrollWidth - clientWidth)
        setScrollState((s) => {
            if (s.left === left && s.right === right) {
                return s
            }
            return {
                left,
                right,
            }
        })
    }, [])

    // update gradients on resize
    const scrollRef = useRef<HTMLDivElement | null>(null)
    const ref = useRef<HTMLDivElement | null>(null)
    useResizeObserver(ref as RefObject<HTMLElement>, (entry) => {
        scrollRef.current?.dispatchEvent(new Event('scroll'))
    })

    return (
        <Stack
            minHeight={{ default: 'x10', touch: 'x7' }}
            position="relative"
            overflow="hidden"
            ref={ref}
        >
            <Stack
                absoluteFill
                horizontal
                ref={scrollRef}
                minHeight={{ default: 'x10', touch: 'x7' }}
                position="relative"
                overflow="scroll"
                gap={props.children ? 'md' : 'none'}
                onScroll={onScroll}
            >
                {props.children}
            </Stack>
            <AnimatePresence>
                {scrollState.left && (
                    <FadeInBox
                        pointerEvents="none"
                        position="topLeft"
                        width={{ default: 'x10', touch: 'x7' }}
                        height={{ default: 'x10', touch: 'x7' }}
                        style={{
                            backgroundImage: `linear-gradient(to left, ${vars.color.background.level1Opacity0}, var(--background))`,
                            x: -1.5,
                        }}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {scrollState.right && (
                    <FadeInBox
                        fast
                        pointerEvents="none"
                        position="topRight"
                        width={{ default: 'x10', touch: 'x7' }}
                        height={{ default: 'x10', touch: 'x7' }}
                        style={{
                            backgroundImage: `linear-gradient(to right, ${vars.color.background.level1Opacity0}, var(--background))`,
                            x: 1.5,
                        }}
                    />
                )}
            </AnimatePresence>
        </Stack>
    )
}
